import { Box, Prose, Text } from '@factorialco/deprecated-design-system'
import type {
  PerformanceInsightsQuantityEnum,
  PerformanceTargetInsightsDetailsFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

import AiFeedbackRequest from 'modules/ai/components/FeedbackRequest'

import { Insight } from '../TargetCard'

const ContentBox: React.FC<{
  title: string
  content: NonNullable<React.ReactNode>
  insight?: PerformanceInsightsQuantityEnum | null
}> = ({ title, content, insight }) => (
  <Box gap="s8">
    <Text size="400" weight="semibold">
      {title}
    </Text>
    {insight && <Insight value={insight} />}
    <Prose>{content}</Prose>
  </Box>
)

export const TargetInsightsDetails: React.FC<{
  target: PerformanceTargetInsightsDetailsFragment
}> = ({ target: { insight, access } }) => {
  if (!insight || insight.empty) {
    return (
      <Box gap="s32">
        <ContentBox
          title={i18n.t('performance_process.insights.titles.performance_summary')}
          content={i18n.t('performance_process.insights.empty_target_insights')}
        />
      </Box>
    )
  }

  const {
    performanceSummary,
    alignmentSummary,
    performanceScore,
    alignmentScore,
    strengths,
    weaknesses,
    actionItems,
  } = insight

  return (
    <Box gap="s32">
      {performanceSummary && (
        <ContentBox
          title={i18n.t('performance_process.insights.titles.performance')}
          content={performanceSummary}
          insight={performanceScore}
        />
      )}
      {alignmentSummary && (
        <ContentBox
          title={i18n.t('performance_process.insights.titles.alignment')}
          content={alignmentSummary}
          insight={alignmentScore}
        />
      )}

      {strengths && (
        <ContentBox
          title={i18n.t('performance_process.insights.titles.strengths')}
          content={strengths}
        />
      )}
      {weaknesses && (
        <ContentBox
          title={i18n.t('performance_process.insights.titles.areas_to_improve')}
          content={weaknesses}
        />
      )}
      {actionItems && actionItems.length > 0 && (
        <ContentBox
          title={i18n.t('performance_process.insights.titles.suggested_action_plan', {
            employee: access?.fullName ?? i18n.t('performance_process.insights.titles.employee'),
          })}
          content={
            <ul>
              {actionItems.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          }
        />
      )}
      <Prose>
        <AiFeedbackRequest feedbackType="performance.target_insight.page" resourceId={insight.id} />
      </Prose>
    </Box>
  )
}
